import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Building2,
  Truck,
  MapPin,
  Clock,
  Coffee,
  Sparkles,
  Users,
  PackageCheck,
  CalendarCheck,
  Check,
  ArrowRight,
} from "lucide-react";
import { Button } from "../ui/button";

const options = [
  {
    id: "estudio",
    tag: "Opción 1",
    icon: Building2,
    title: "Grabas en nuestro estudio",
    location: "C/ Fernando el Católico 57, local — Madrid",
    mapsUrl:
      "https://www.google.com/maps/search/?api=1&query=Calle+Fernando+el+Cat%C3%B3lico+57%2C+28015+Madrid",
    pitch:
      "Llegas, te ponemos un café y grabas. Todo está montado y calibrado antes de que cruces la puerta: cámaras, luces, micros y sala tratada acústicamente. Tú solo tienes que hablar de lo que sabes.",
    perfectFor: "Ideal si quieres máxima calidad de imagen y sonido, y desconectar de la oficina.",
    points: [
      { icon: Sparkles, text: "Set fijo con iluminación cinemática y fondo de marca personalizable" },
      { icon: Clock, text: "En 90 minutos grabamos hasta 4 episodios del tirón" },
      { icon: Users, text: "Espacio cómodo para 2-4 personas: invitados, socios o clientes" },
      { icon: Coffee, text: "Café, agua y una persona guiando la sesión de principio a fin" },
    ],
    accent: "from-galvani-blue to-galvani-purple",
    badge: "El favorito de nuestros clientes",
  },
  {
    id: "desplazamiento",
    tag: "Opción 2",
    icon: Truck,
    title: "Nos desplazamos a tu ubicación",
    location: "Tu oficina, tu fábrica o tu showroom (Madrid y 70 km alrededor)",
    mapsUrl: null,
    pitch:
      "Montamos un estudio completo donde tú digas. Llegamos antes, instalamos todo y desmontamos sin dejar rastro. Tu equipo no pierde el día y tu marca aparece en su propio entorno real.",
    perfectFor:
      "Ideal si tu equipo tiene agenda apretada, o si quieres mostrar tus instalaciones y tu día a día.",
    points: [
      { icon: PackageCheck, text: "Estudio portátil: multicámara 4K, luces y sonido de estudio" },
      { icon: MapPin, text: "Comunidad de Madrid y hasta 70 km alrededor" },
      { icon: CalendarCheck, text: "Nos adaptamos a tu agenda: mañana, tarde o día completo" },
      { icon: Users, text: "Perfecto para entrevistar a varios directivos en una sola jornada" },
    ],
    accent: "from-galvani-cyan to-galvani-blue",
    badge: "Sin que nadie salga de la oficina",
  },
];

const shared = [
  "Misma calidad de producción en ambas opciones",
  "Guion y preguntas preparados por nosotros",
  "Edición, clips verticales y miniaturas incluidos",
];

export default function RecordingOptions() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const goToForm = () => {
    document.getElementById("solicitar-propuesta")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="donde-grabamos"
      className="section-spacing bg-white relative overflow-hidden"
      data-testid="recording-options-section"
    >
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-200/25 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-purple-200/25 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
            Dónde grabamos
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-5">
            Tú eliges dónde grabar.{" "}
            <span className="gradient-text">Nosotros nos encargamos del resto</span>
          </h2>
          <p className="font-body text-base text-galvani-textLight leading-relaxed">
            No queremos que la logística sea la excusa para no lanzar tu videopodcast. Puedes venir a
            nuestro estudio en el centro de Madrid o hacer que el estudio vaya a ti. El resultado final
            es exactamente el mismo: contenido con acabado profesional, listo para publicar.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {options.map((opt, i) => (
            <motion.div
              key={opt.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.15 }}
              className="group relative flex flex-col rounded-3xl bg-white border border-slate-200 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 overflow-hidden"
              data-testid={`recording-option-${opt.id}`}
            >
              <div className={`h-1.5 w-full bg-gradient-to-r ${opt.accent}`} />

              <div className="p-7 md:p-9 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${opt.accent} flex items-center justify-center shadow-md flex-shrink-0`}
                  >
                    <opt.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.18em] font-body font-semibold text-galvani-textLight pt-2">
                    {opt.tag}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-2xl text-galvani-text mb-2">
                  {opt.title}
                </h3>

                <div className="flex items-start gap-2 mb-5">
                  <MapPin className="w-4 h-4 text-galvani-pink mt-0.5 flex-shrink-0" />
                  {opt.mapsUrl ? (
                    <a
                      href={opt.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-sm font-medium text-galvani-purple hover:underline"
                      data-testid="studio-maps-link"
                    >
                      {opt.location}
                    </a>
                  ) : (
                    <span className="font-body text-sm font-medium text-galvani-text">
                      {opt.location}
                    </span>
                  )}
                </div>

                <p className="font-body text-sm text-galvani-textLight leading-relaxed mb-6">
                  {opt.pitch}
                </p>

                <ul className="space-y-3 mb-6">
                  {opt.points.map((p) => (
                    <li key={p.text} className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <p.icon className="w-4 h-4 text-galvani-blue" />
                      </span>
                      <span className="font-body text-sm text-galvani-text leading-relaxed pt-1.5">
                        {p.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="font-body text-sm text-galvani-text bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 rounded-xl p-4 mb-7">
                  <strong className="font-semibold">{opt.badge}.</strong> {opt.perfectFor}
                </p>

                <Button
                  onClick={goToForm}
                  className={`mt-auto w-full bg-gradient-to-r ${opt.accent} hover:opacity-90 text-white font-body font-semibold h-12 rounded-full shadow-md`}
                  data-testid={`recording-option-cta-${opt.id}`}
                >
                  Quiero esta opción
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 flex flex-col md:flex-row md:items-center md:justify-center gap-4 md:gap-8 p-6 rounded-2xl bg-galvani-bg border border-slate-200"
          data-testid="recording-options-shared"
        >
          {shared.map((s) => (
            <div key={s} className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-green-600" />
              </span>
              <span className="font-body text-sm text-galvani-text">{s}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
